import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarkerAlt,
    faMoneyBillWave,
    faBriefcaseMedical,
    faArrowRight,
    faSearch,
    faUserMd,
    faSpinner,
    faExclamationTriangle,
    faChevronLeft,
    faChevronRight,
    faClock ,
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../Navbar/Navbar'; // Assuming Navbar path is correct
import Footer from '../Footer/Footer'; // Assuming Footer path is correct
import patientImg from '../../assets/img/patients/patient.jpg'; // Default image
// Optional: A nice background for the hero section
// import heroBgImage from '../../assets/img/hero-doctors-bg.jpg'; 
import './listdoctor.css'; // Your new CSS file

 const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.hitkarihospital.com:5005';
const DOCTORS_PER_PAGE = 6; // Number of doctors to display per page

const ListDoctor = () => {
    const [allDoctors, setAllDoctors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        axios.get(`${API_BASE_URL}/api/doctor`)
            .then(response => {
                setAllDoctors(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('There was an error fetching the doctor data!', error);
                setError('Failed to load doctors. Please try again later.');
                setLoading(false);
            });
    }, []);

    const getDoctorImageUrl = (imagePath) => {
        if (!imagePath) return patientImg;
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        return `${API_BASE_URL}/doctor/${imagePath}`;
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const filteredDoctors = useMemo(() => {
        if (!searchTerm) {
            return allDoctors;
        }
        return allDoctors.filter(doctor =>
            (doctor.name && doctor.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (doctor.lastName && doctor.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (doctor.speciality && doctor.speciality.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (doctor.address && doctor.address.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [allDoctors, searchTerm]);

    const indexOfLastDoctor = currentPage * DOCTORS_PER_PAGE;
    const indexOfFirstDoctor = indexOfLastDoctor - DOCTORS_PER_PAGE;
    const currentDoctors = filteredDoctors.slice(indexOfFirstDoctor, indexOfLastDoctor);
    const totalPages = Math.ceil(filteredDoctors.length / DOCTORS_PER_PAGE);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            const listTop = document.getElementById('list-doctors-list')?.offsetTop || 0;
            window.scrollTo({ top: listTop - 100, behavior: 'smooth' }); // Scroll to above list
        }
    };

    const renderPaginationNumbers = () => {
        const pageNumbers = [];
        const maxPageButtons = 5; 

        if (totalPages <= maxPageButtons) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
            let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

            if (endPage - startPage + 1 < maxPageButtons) {
                 startPage = Math.max(1, endPage - maxPageButtons + 1);
            }
            
            if (startPage > 1) {
                pageNumbers.push(1);
                if (startPage > 2) pageNumbers.push('...');
            }

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            if (endPage < totalPages) {
                if (endPage < totalPages - 1) pageNumbers.push('...');
                pageNumbers.push(totalPages);
            }
        }
        
        return pageNumbers.map((number, index) =>
            typeof number === 'number' ? (
                <li key={index} className={`list-page-item ${currentPage === number ? 'list-active' : ''}`}>
                    <button onClick={() => paginate(number)} className="list-page-link">
                        {number}
                    </button>
                </li>
            ) : (
                <li key={index} className="list-page-item list-disabled"><span className="list-page-link list-ellipsis">{number}</span></li>
            )
        );
    };
const formatTime12Hour = (time24) => {
  if (!time24) return 'N/A'; // Handle case where time is not available

  // Split the time string into hours and minutes
  const [hours, minutes] = time24.split(':');

  // Convert hours from string to number
  let hour = parseInt(hours, 10);

  // Determine AM or PM
  const ampm = hour >= 12 ? 'PM' : 'AM';

  // Convert hour to 12-hour format
  hour = hour % 12;
  // If hour is 0 (e.g., 12 AM), it should be 12
  hour = hour ? hour : 12;

  // Return the formatted time string
  return `${hour}:${minutes} ${ampm}`;
};

    return (
        <>
            <div className="list-page-container" id="list-doctor-page">
                <header className="list-main-header" id="list-header">
                    <Navbar />
                </header>

                <section 
                    className="list-hero-breadcrumb-section" 
                    id="list-hero"
                    // style={heroBgImage ? {backgroundImage: `url(${heroBgImage})`} : {}} // Optional BG image
                >
                    <div className="list-hero-overlay"></div> {/* Overlay for better text readability if BG image is used */}
                    <div className="list-container list-hero-content">
                        <nav aria-label="breadcrumb" className="list-breadcrumb-nav">
                            <ol className="list-breadcrumb-list">
                                <li className="list-breadcrumb-item"><a href="/">Home</a></li>
                                <li className="list-breadcrumb-item list-breadcrumb-current" aria-current="page">Find a Doctor</li>
                            </ol>
                        </nav>
                        <h1 className="list-page-main-title">Discover Your Ideal Healthcare Professional</h1>
                        <p className="list-page-subtitle">
                            Connect with experienced specialists dedicated to your well-being.
                        </p>
                    </div>
                </section>

                <main className="list-content-area" id="list-main-content">
                    <div className="list-container">
                        <div className="list-search-filter-bar" id="list-search-bar-wrapper">
                            <div className="list-search-input-wrapper">
                                <FontAwesomeIcon icon={faSearch} className="list-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by name, specialty, or location..."
                                    className="list-search-input"
                                    id="list-doctor-search"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        </div>

                        {loading && (
                            <div className="list-feedback-container list-loader-container" id="list-loading-state">
                                <FontAwesomeIcon icon={faSpinner} spin size="3x" className="list-feedback-icon" />
                                <p className="list-feedback-text">Loading Doctors...</p>
                            </div>
                        )}

                        {error && (
                            <div className="list-feedback-container list-error-container" id="list-error-state">
                                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="list-feedback-icon"/>
                                <p className="list-feedback-text">{error}</p>
                            </div>
                        )}

                         {!loading && !error && (
                            <div className="list-doctors-listing-wrapper" id="list-listing-wrapper">
                                {currentDoctors.length > 0 ? (
                                    <div className="list-doctors-list-container" id="list-doctors-list">
                                        {currentDoctors.map(doctor => {
                                            
                                            // --- FIX: Logic to get correct timings is now INSIDE the map ---
                                            const availableSlots = doctor.defaultAvailability?.filter(slot => slot.isAvailable) ?? [];

                                            return (
                                                <article className="list-doctor-list-item" key={doctor.doctorId}>
                                                    <div className="list-doctor-item-image-wrapper">
                                                        <a href={`/doctor-profile/${doctor.doctorId}`} className="list-doctor-image-link">
                                                            <img
                                                                src={getDoctorImageUrl(doctor.image)}
                                                                alt={`Dr. ${doctor.name}`}
                                                                className="list-doctor-avatar"
                                                                onError={(e) => { e.target.onerror = null; e.target.src = patientImg; }}
                                                            />
                                                        </a>
                                                    </div>
                                                    
                                                    <div className="list-doctor-item-details">
                                                        <h2 className="list-doctor-name">
                                                            <a href={`/doctor-profile/${doctor.doctorId}`}> {doctor.name}</a>
                                                        </h2>
                                                        <p className="list-doctor-specialty">
                                                            <FontAwesomeIcon icon={faBriefcaseMedical} className="list-detail-icon" />
                                                            {doctor.speciality || 'N/A'}
                                                        </p>
                                                        
                                                        {/* --- UPDATED JSX BLOCK for Timings --- */}
                                                        <div className="list-doctor-meta-info">
                                                          {availableSlots.length > 0 ? (
                                                              availableSlots.map((slot, idx) => (
                                                                  <span key={idx} className="list-meta-item timming">
                                                                    <FontAwesomeIcon 
                                                                        icon={faClock} 
                                                                        className="list-detail-icon"
                                                                    />
                                                                    {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                                                                  </span>
                                                              ))
                                                          ) : (
                                                              <span className="list-meta-item timming-not-set">
                                                                  <FontAwesomeIcon icon={faClock} className="list-detail-icon" />
                                                                  Availability not set
                                                              </span>
                                                          )}
                                                        </div>
                                                        {/* --- END OF UPDATED JSX BLOCK --- */}

                                                        <div className="list-doctor-meta-info">
                                                            <span className="list-meta-item list-doctor-location">
                                                                <FontAwesomeIcon icon={faMapMarkerAlt} className="list-detail-icon" />
                                                                {doctor.address ? doctor.address.split(',').slice(0,2).join(', ') : 'Location N/A'}
                                                            </span>
                                                            <span className="list-meta-item list-doctor-price">
                                                                <FontAwesomeIcon icon={faMoneyBillWave} className="list-detail-icon" />
                                                                Consultation: ₹{doctor.price ?? 'N/A'}
                                                            </span>
                                                        </div>
                                                        
                                                        {doctor.bio && (
                                                            <p className="list-doctor-bio">
                                                                {doctor.bio.substring(0, 150)}{doctor.bio.length > 150 ? '...' : ''}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="list-doctor-item-actions">
                                                        <a href={`/doctor-profile/${doctor.doctorId}`} className="list-btn list-btn-outline">
                                                            View Profile
                                                        </a>
                                                        <a href={`/patientlogin/${doctor.doctorId}`} className="list-btn list-btn-primary">
                                                            Book Appointment
                                                            <FontAwesomeIcon icon={faArrowRight} className="list-btn-icon-right" />
                                                        </a>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="list-feedback-container list-no-doctors-found-message" id="list-empty-state">
                                         <FontAwesomeIcon icon={faUserMd} size="3x" className="list-feedback-icon list-no-results-icon"/>
                                        <p className="list-feedback-text">No doctors found matching your criteria. Please try a different search.</p>
                                    </div>
                                )}

                                {totalPages > 1 && !loading && !error && filteredDoctors.length > 0 && (
                                    <nav className="list-pagination-container" aria-label="Page navigation" id="list-pagination">
                                        <ul className="list-pagination-list">
                                            <li className={`list-page-item ${currentPage === 1 ? 'list-disabled' : ''}`}>
                                                <button onClick={() => paginate(currentPage - 1)} className="list-page-link list-page-prev" aria-label="Previous">
                                                    <FontAwesomeIcon icon={faChevronLeft} />
                                                </button>
                                            </li>
                                            {renderPaginationNumbers()}
                                            <li className={`list-page-item ${currentPage === totalPages ? 'list-disabled' : ''}`}>
                                                <button onClick={() => paginate(currentPage + 1)} className="list-page-link list-page-next" aria-label="Next">
                                                    <FontAwesomeIcon icon={faChevronRight} />
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                )}
                            </div>
                        )}
                    </div>
                </main>
            </div>
            <Footer />
        </>
    );
};

export default ListDoctor;